import axios, { AxiosResponse, AxiosError, AxiosProgressEvent } from "axios";
import { getSession, signOut } from "next-auth/react";
import { getMemoryToken, setMemoryToken } from "@/utils/tokenManager";
import { shouldHandle401 } from "@/utils/auth-context";

// Requests go through our own /api/proxy route, which injects the real backend
// credentials server-side — the browser bundle never sees them.
const host = "/api/proxy";

const axiosClient = axios.create({
    baseURL: host,
});

// Helper function to get token from Memory Cache, fallback to NextAuth getSession
async function getValidToken(): Promise<string> {
    let token = getMemoryToken();
    if (!token) {
        // Fallback for initial load (when cache is empty)
        const session = await getSession();
        if (session?.accessToken) {
            token = session.accessToken as string;
            setMemoryToken(token);
        } else {
            // If getSession() returns null/empty but the HTTP-only cookie might still be valid,
            // force a direct fetch to the server to bypass client-side cache.
            const forceToken = await refreshToken();
            if (forceToken) {
                token = forceToken;
            }
        }
    }
    return token || 'null';
}

const refreshToken = async (): Promise<string | null> => {
    try {
        // Calling getSession() directly from next-auth/react might return a cached session 
        // that doesn't know the token has expired. To force a server-side evaluation,
        // we fetch the session endpoint directly.
        const res = await fetch('/api/auth/session');
        const session = await res.json();
        
        // If NextAuth reports an error (e.g. RefreshAccessTokenError), the refresh failed.
        if (session?.error) {
            return null;
        }

        if (session?.accessToken) {
            const newToken = session.accessToken as string;
            setMemoryToken(newToken);
            
            // Dispatch event to force NextAuth hooks (useSession) to sync the new token
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event("visibilitychange"));
            }
            
            return newToken;
        }
        return null;
    } catch {
        return null;
    }
};

// Add request interceptor to include auth token from Memory Cache
axiosClient.interceptors.request.use(
    async (config) => {
        const token = await getValidToken();
        if (token && token !== 'null') {
            config.headers.Authorization = `Bearer ${token}`;
            if (!config.headers['access-token']) {
                config.headers['access-token'] = token;
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add response interceptor to handle 401 token refresh cleanly
axiosClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            const requestUrl = String(originalRequest?.url || '');
            const requestPath = requestUrl.split(/[?#]/)[0].replace(/\/+$/, '');
            // Do not force logout if the 401 comes from a public-facing service call on a guest page
            // Admin /gemini-usage/* statistics and budgets must still refresh sessions.
            if (requestPath.endsWith('/v1/general/services/tts') ||
                requestPath.endsWith('/v1/general/services/budget/status')) {
                return Promise.reject(error);
            }

            // [FIX PHASE A]: Only handle 401 auto-redirect in Admin/Project Admin context
            if (typeof window !== 'undefined' && !shouldHandle401(window.location.pathname)) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            const oldToken = originalRequest.headers.Authorization?.replace('Bearer ', '');
            const newToken = await refreshToken();

            if (newToken && newToken !== oldToken) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                if (originalRequest.headers['access-token']) {
                    originalRequest.headers['access-token'] = newToken;
                }
                return axiosClient(originalRequest);
            } else if (newToken === null) {
                // If we cannot get a new token (refresh failed), clear session completely
                if (typeof window !== 'undefined') {
                    localStorage.removeItem('isLoggedIn');
                    sessionStorage.clear();

                    // Dispatch event to show beautiful toast on current screen
                    if (!(window as any).__isLoggingOut) {
                        (window as any).__isLoggingOut = true;
                        window.dispatchEvent(new CustomEvent('session_expired_event'));

                        // Calculate dynamic login path
                        const pathname = window.location.pathname;
                        const segments = pathname.split('/').filter(Boolean);
                        const adminIndex = segments.indexOf('admin');

                        let loginUrl = '/admin/login';
                        if (adminIndex !== -1) {
                            loginUrl = `/${segments.slice(0, adminIndex + 1).join('/')}/login`;
                        }

                        // Delay signout to let user see the message
                        setTimeout(async () => {
                            await signOut({ redirect: false });
                            (window as any).__isLoggingOut = false;
                            window.location.href = loginUrl + '?expired=true';
                        }, 2500);
                    }
                } else {
                    signOut({ redirect: false });
                }
                return Promise.reject(error);
            } else {
                // newToken === oldToken.
                // NextAuth considers the token valid, but the API returned 401.
                // Do NOT logout the user, as the 401 might be due to a proxy referer check, permission issue, or other non-auth reason.
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);



// Utility to handle responses consistently
async function handleRequest<T>(promise: Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> {
    try {
        const response = await promise; // Successful request
        return response; // Return the full Axios response
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
            // Server responded with a non-2xx status code
            return axiosError.response as AxiosResponse<T>; // Return the error response
        }
        console.error("Network or unexpected error:", axiosError.message, {
            method: axiosError.config?.method,
            url: axiosError.config?.url,
            baseURL: axiosError.config?.baseURL,
            params: axiosError.config?.params,
        });
        throw new Error("Network error or no response from server.");
    }
}

interface RequestParams {
    endpoint: string;
    headers?: Record<string, string>;
}

interface BodyRequestParams extends RequestParams {
    body?: unknown;
}

interface StreamRequestParams extends BodyRequestParams {
    onStream?: (progressEvent: AxiosProgressEvent) => void;
}

export async function baseGet<T>({ endpoint, headers }: RequestParams): Promise<AxiosResponse<T>> {
    const token = await getValidToken();
    try {
        // Create URL object to properly handle parameters
        const url = new URL(endpoint, host);
        const finalEndpoint = `${url.pathname}${url.search}`;
        const finalHeaders = {
            ...headers,
            'access-token': token
        };

        return handleRequest<T>(axiosClient.get(finalEndpoint, { headers: finalHeaders }));
    } catch {
        // Fallback for relative URLs
        const finalHeaders = {
            ...headers,
            'access-token': token
        };

        return handleRequest<T>(axiosClient.get(endpoint, { headers: finalHeaders }));
    }
}

export async function basePost<T>({ endpoint, headers, body }: BodyRequestParams): Promise<AxiosResponse<T>> {
    const token = await getValidToken();
    const finalHeaders = {
        ...headers,
        'access-token': token
    };

    return handleRequest<T>(axiosClient.post(endpoint, body, { headers: finalHeaders }));
}

// Handle Stream request (onStream is passed from outside)
export async function basePostStream<T>({ endpoint, headers, body, onStream }: StreamRequestParams): Promise<AxiosResponse<T>> {
    const token = await getValidToken();
    const finalHeaders = {
        ...headers,
        'access-token': token
    };

    return handleRequest<T>(
        axios({
            method: "post",
            headers: finalHeaders,
            url: host + endpoint,
            data: body,
            responseType: "stream",
            onDownloadProgress: onStream,
        })
    );
}

export async function basePut<T>({ endpoint, headers, body }: BodyRequestParams): Promise<AxiosResponse<T>> {
    const token = await getValidToken();
    const finalHeaders = {
        ...headers,
        'access-token': token
    };
    
    return handleRequest<T>(axiosClient.put(endpoint, body, { headers: finalHeaders }));
}

export async function basePatch<T>({ endpoint, headers, body }: BodyRequestParams): Promise<AxiosResponse<T>> {
    const token = await getValidToken();
    const finalHeaders = {
        ...headers,
        'access-token': token
    };
    
    return handleRequest<T>(axiosClient.patch(endpoint, body, { headers: finalHeaders }));
}

export async function baseDelete<T>({ endpoint, headers, body }: BodyRequestParams): Promise<AxiosResponse<T>> {
    const token = await getValidToken();
    const finalHeaders = {
        ...headers,
        'access-token': token
    };

    return handleRequest<T>(axiosClient.delete(endpoint, { headers: finalHeaders, data: body }));
}

// Special function for downloading blob files (Excel, PDF, etc.)
export async function baseGetBlob({ endpoint, headers }: RequestParams): Promise<AxiosResponse<Blob>> {
    const token = await getValidToken();
    try {
        // Create URL object to properly handle parameters
        const url = new URL(endpoint, host);
        const finalEndpoint = `${url.pathname}${url.search}`;
        const finalHeaders = {
            ...headers,
            'access-token': token
        };
        
        return handleRequest<Blob>(axiosClient.get(finalEndpoint, {
            headers: finalHeaders,
            responseType: 'blob',
        }));
    } catch {
        // Fallback for relative URLs
        const finalHeaders = {
            ...headers,
            'access-token': token
        };
        
        return handleRequest<Blob>(axiosClient.get(endpoint, {
            headers: finalHeaders,
            responseType: 'blob',
        }));
    }
}

// Special function for posting and receiving blob files
export async function basePostBlob({ endpoint, headers, body }: BodyRequestParams): Promise<AxiosResponse<Blob>> {
    const token = await getValidToken();
    const finalHeaders = {
        ...headers,
        'access-token': token
    };
    
    return handleRequest<Blob>(axiosClient.post(endpoint, body, {
        headers: finalHeaders,
        responseType: 'blob',
    }));
}

// Special function for uploading files with FormData
export async function basePostFile<T>({ endpoint, body }: { endpoint: string; body: FormData }): Promise<AxiosResponse<T>> {
    const token = await getValidToken();
    const finalHeaders = {
        'access-token': token
        // Don't set Content-Type - let browser set it with boundary for FormData
    };
    
    return handleRequest<T>(axiosClient.post(endpoint, body, { headers: finalHeaders }));
}

export default axiosClient;
